import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiErr, fmtDate } from "../SuperHeader";
import { useAdminFetch, TabState, Stat } from "../useAdminFetch";
import Pager from "../Pager";
import ConfirmDialog from "../ConfirmDialog";

export function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export default function ContactsTab({ cid }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const dq = useDebounced(q);
  useEffect(() => setPage(1), [dq]);
  const { data, error, loading, reload } = useAdminFetch(`/admin/companies/${cid}/contacts`, { q: dq || undefined, page });
  const [confirm, setConfirm] = useState(null);
  const del = (c) => setConfirm({
    title: `Permanently delete contact ${c.name} (${c.phone})?`, body: "This cannot be undone.", danger: true, label: "Delete",
    run: async () => { try { await api.delete(`/admin/companies/${cid}/contacts/${c.id}`); toast.success("Contact deleted"); reload(); } catch (e) { toast.error(apiErr(e)); } },
  });
  return (
    <Card className="p-4 border-slate-200 space-y-3" data-testid="contacts-tab">
      {data && <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Total" value={data.stats.total} testid="contacts-total" /><Stat label="Opted in" value={data.stats.opted_in} />
        <Stat label="Opted out" value={data.stats.opted_out} /><Stat label="Consent unknown" value={data.stats.unknown} />
      </div>}
      <Input placeholder="Search name, phone or email" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="contacts-search" />
      <TabState loading={loading && !data} error={error} empty={data?.items.length === 0} emptyText="No contacts found." testid="contacts">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="p-2 text-left">Name</th><th className="p-2 text-left">Phone</th><th className="p-2 text-left">Consent</th><th className="p-2 text-left">Source</th><th className="p-2 text-left">Created</th><th /></tr></thead>
            <tbody>{(data?.items || []).map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`contact-row-${c.id}`}>
                <td className="p-2">{c.name}<div className="text-[11px] text-slate-500">{c.email}</div></td>
                <td className="p-2 font-mono text-xs">{c.phone}</td>
                <td className="p-2"><Badge variant="outline">{c.consent_status || "unknown"}</Badge></td>
                <td className="p-2 text-xs">{c.source || "—"}</td>
                <td className="p-2 text-xs text-slate-500">{fmtDate(c.created_at)}</td>
                <td className="p-2 text-right"><Button size="icon" variant="ghost" onClick={() => del(c)} data-testid={`contact-delete-${c.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button></td>
              </tr>))}</tbody>
          </table>
        </div>
      </TabState>
      {data && data.total > 0 && <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} testid="contacts-pager" />}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />}
    </Card>
  );
}
